/**
 * Where a deep-link came from, and how to get back.
 *
 * The app already spoke `?from=<path>&from_label=<name>` — the model chips write
 * it and Settings reads it — but the reading half lived inside Settings, so
 * every other destination you can be deep-linked INTO was a dead end. The page
 * builder now sends you to a room, a subject and a document library to check
 * what is feeding a build, and landing in any of them with no way back is the
 * same trap the trends page hit when a brainstorm sent you to check a seed.
 *
 * Dependency-free so the guard below can be tested — CRA's jest cannot resolve
 * react-router-dom v7.
 */

/**
 * The `from` param, if it is safe to navigate to.
 *
 * ONLY IN-APP PATHS. A `from` pointing off-site, or protocol-relative
 * ("//evil.example"), is an open redirect wearing a back button — anyone who can
 * get a user to click a crafted link picks where "back" goes.
 */
export function safeReturnPath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/')) return null;
  // "//host" is protocol-relative and leaves the site.
  if (raw.startsWith('//')) return null;
  // A backslash is treated as a slash by some parsers; "/\evil.example" has
  // escaped before.
  if (raw.startsWith('/\\')) return null;
  return raw;
}

/** Append a return trail to an in-app URL. */
export function withReturnTo(
  url: string,
  from: string | null | undefined,
  label?: string,
): string {
  if (!from) return url;
  const sep = url.includes('?') ? '&' : '?';
  const params = new URLSearchParams({ from });
  if (label) params.set('from_label', label);
  return `${url}${sep}${params.toString()}`;
}

/** Carry an existing trail across a navigation within the destination. */
export function keepReturnTo(
  params: URLSearchParams,
  next: URLSearchParams,
): URLSearchParams {
  const from = safeReturnPath(params.get('from'));
  if (!from) return next;
  next.set('from', from);
  const label = params.get('from_label');
  if (label) next.set('from_label', label);
  return next;
}

/**
 * What to call the place a return trail points back to.
 *
 * IT HAS TO NAME THE PAGE, not the app. The account drawer stamped the trail
 * with the product's own name, so every trip to Settings came back offering
 * "Back to Lumnian" — true, useless, and identical whether you had come from
 * Trends or from a half-finished page build. The destination was always right;
 * only the word for it was wrong.
 *
 * Longest match wins, for the same reason the nav highlight does: `/deliver` and
 * `/deliver/review` both match a route under the latter, and the more specific
 * one is the one that describes where you are.
 *
 * Sections are passed in rather than imported so this stays dependency-free —
 * CRA's jest cannot resolve the routed component that owns them.
 */
export interface LabelledPath {
  path: string;
  label: string;
  extraPaths?: string[];
}

export interface LabelledSection {
  label: string;
  items: LabelledPath[];
}

export function returnLabelFor(
  sections: LabelledSection[],
  pathname: string,
  fallback: string,
): string {
  const matches = (item: LabelledPath) =>
    item.path === '/'
      ? pathname === '/'
      : pathname.startsWith(item.path) ||
        (item.extraPaths ?? []).some((p) => pathname.startsWith(p));

  let best: LabelledPath | null = null;
  for (const section of sections) {
    for (const item of section.items) {
      if (!matches(item)) continue;
      if (!best || item.path.length > best.path.length) best = item;
    }
  }
  // Routes that sit outside the workflow entirely — Settings, your account —
  // match nothing, and the app's name is the honest answer for those.
  return best ? best.label : fallback;
}
